/** @prettier */

import {Grid, Box, Stack} from '@mui/material';
import {useEffect, useState, ReactElement} from 'react';
import {useParams} from 'react-router-dom';
import {isUndefined} from 'lodash';
import {useTranslation} from 'react-i18next';

//API import
import {
  getStatesSitesRt,
  getStatesSitesModbus,
  getSettingUser,
} from '../../API/API_Collection';
//interface import
import * as apiIF from '../../Type/api';
//Style import
import '../../css/common/common.css';

import {
  invertComma,
  initialUpper,
  roundOff,
} from '../../functions/convertString';
import {useValueRef} from '../../functions/useValueRef';
import {iconBlack, iconWhite} from '../../define';

/**
 * HCCVQDetail コンポーネント
 * @param props props
 * - setISPaidGW - 対象のユニットが登録されているGWの課金状態。無課金の場合はFalse
 */
const HCCVQDetail = (props: {
  setISPaidGW: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const trans = useTranslation().t; //翻訳関数
  /* URLパラメータの取得 */
  const {siteID, targetID: unitID} = useParams();

  //空調機の種類
  const [unitType, setUnitType] = useState<'' | 'hyozan' | 'modbus'>('');
  //空調機の名前
  const [unitName, setUnitName] = useState('');
  //prettier-ignore
  //運転停止
  const [runStop, setRunStop] = useState<apiIF.resParameters.rtOUStates['run-stop'] | apiIF.resParameters.modbusOUStates['run-stop']>(undefined);
  //蒸発温度
  const [evaporating, setEvaporating] = useState('');
  //コンプ高圧
  const [highPressure, setHighPressure] = useState('');
  //コンプ低圧
  const [lowPressure, setLowPressure] = useState('');
  //目標コンプ低圧
  const [targetLowPressure, setTargetLowPressure] = useState('');
  //エラー
  const [error, setError] = useState('');
  //現在時刻（状態値の定期更新用）
  const [nowTime, setNowTime] = useState(new Date());
  //圧力単位
  const [pressureUnit, setPressureUnit] =
    useState<apiIF.res.getSettingUser['unit']['pressure']>('MPa');

  //状態値検索用クエリ
  const hyozanStateQuery: apiIF.reqParameters.getStatesSitesRt = {
    fields:
      'unit-name,run-stop,evaporating-temperature,high-pressure,low-pressure,target-low-pressure,connection-status,error-code',
  };
  const modbusStateQuery: apiIF.reqParameters.getStatesSitesModbus = {
    fields: 'outdoor-unit-name,run-stop,high-pressure,low-pressure,error-code',
  };

  useEffect(() => {
    //パスパラメータを取得できない場合
    if (isUndefined(siteID) || isUndefined(unitID)) return;

    //空調機の種類によって処理を切り替える
    if (unitID.startsWith('RT')) {
      setUnitType('hyozan');
      //hyozanの空調機の状態値データを取得
      getStatesSitesRt(siteID, hyozanStateQuery).then(response => {
        if (response['rt-states'].length === 0) return;
        const state = response['rt-states'][0]['outdoor-unit-states'].find(
          data => data['unit-id'] === unitID
        );
        if (state !== undefined) {
          setHyozanState(state);
        }
      });
    } else if (unitID.startsWith('Modbus')) {
      setUnitType('modbus');
      //Modbusの状態値データを取得
      getStatesSitesModbus(siteID, modbusStateQuery).then(response => {
        if (response['modbus-states'].length === 0) return;
        const state = response['modbus-states'][0]['outdoor-unit-states'].find(
          data => data['outdoor-id'] === unitID
        );
        if (state !== undefined) {
          setModbusState(state);
        }
      });
    }

    getSettingUser().then(response => {
      //圧力単位を取得
      setPressureUnit(response['unit'].pressure);
    });
  }, [siteID, unitID]);

  //タイマ動作後にunitIDが変わった場合、1分後に参照するsiteIDはタイマ動作開始時のunitIDとなるため
  //useRefを使って、現在のunitIDを参照するようにしている
  const nowUnitID = useValueRef(unitID);
  useEffect(() => {
    //パスパラメータを取得できない場合
    if (isUndefined(siteID)) return;

    const intervalId = setInterval(() => {
      //unitIDを取得できない場合
      if (isUndefined(nowUnitID.current)) return;

      setNowTime(new Date());
      if (nowUnitID.current.startsWith('RT')) {
        //空調機の種類によって処理を切り替える
        setUnitType('hyozan');
        //hyozanの空調機の状態値データを取得
        getStatesSitesRt(siteID, hyozanStateQuery).then(response => {
          if (response['rt-states'].length === 0) return;
          const state = response['rt-states'][0]['outdoor-unit-states'].find(
            data => data['unit-id'] === nowUnitID.current
          );
          if (state !== undefined) {
            setHyozanState(state);
          }
        });
      } else if (nowUnitID.current.startsWith('Modbus')) {
        setUnitType('modbus');
        //Modbusの状態値データを取得
        getStatesSitesModbus(siteID, modbusStateQuery).then(response => {
          if (response['modbus-states'].length === 0) return;
          const state = response['modbus-states'][0][
            'outdoor-unit-states'
          ].find(data => data['outdoor-id'] === nowUnitID.current);
          if (state !== undefined) {
            setModbusState(state);
          }
        });
      }
    }, 60000);

    return function () {
      clearInterval(intervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nowTime]);

  /**
   * 状態値を基にHyozan用の表示データを設定する関数
   *  @param {apiIF.resParameters.rtOUStates} state - 状態値を表すオブジェクト
   */
  const setHyozanState = (state: apiIF.resParameters.rtOUStates) => {
    setUnitName(state['unit-name'] === undefined ? '' : state['unit-name']);
    setRunStop(state['run-stop']);
    setEvaporating(
      state['evaporating-temperature'] === undefined
        ? ''
        : state['evaporating-temperature']
    );
    setHighPressure(
      state['high-pressure'] === undefined ? '' : state['high-pressure']
    );
    setLowPressure(
      state['low-pressure'] === undefined ? '' : state['low-pressure']
    );
    setTargetLowPressure(
      state['target-low-pressure'] === undefined
        ? ''
        : state['target-low-pressure']
    );
    const error = state['error-code'] === undefined ? '' : state['error-code'];
    //エラーコードが空欄の場合は無課金
    props.setISPaidGW(error !== '');
    setError(error);
  };
  /**
   * 状態値を基にModbus用の表示データを設定する関数
   *  @param {apiIF.resParameters.modbusOUStates} state - 状態値を表すオブジェクト
   */
  const setModbusState = (state: apiIF.resParameters.modbusOUStates) => {
    setUnitName(
      state['outdoor-unit-name'] === undefined ? '' : state['outdoor-unit-name']
    );
    setRunStop(state['run-stop']);
    setHighPressure(
      state['high-pressure'] === undefined ? '' : state['high-pressure']
    );
    setLowPressure(
      state['low-pressure'] === undefined ? '' : state['low-pressure']
    );
    const error = state['error-code'] === undefined ? '' : state['error-code'];
    //エラーコードが空欄の場合は無課金
    props.setISPaidGW(error !== '');
    setError(error);
  };

  /**
   * 運転/停止用アイコン一覧
   * @param runStop 現在の状態
   * @returns アイコン
   */
  const runStopIconSource = (
    runStop: apiIF.resParameters.rtOUStates['run-stop']
  ) => {
    switch (runStop) {
      case 'run':
        return iconWhite.FREEZER_ON.src;
      case 'stop':
        return iconBlack.FREEZER_OFF.src;
      default:
        return iconBlack.MISSING.src;
    }
  };

  /** 運転/停止アイコン用クラス名 */
  const runStopIconClassName = (
    runStopState: apiIF.resParameters.rtOUStates['run-stop']
  ) => {
    switch (runStopState) {
      case 'run':
        return 'unitArea__stateList__state__value__icon__runStopIcon--run';
      case 'stop':
        return 'unitArea__stateList__state__value__icon__runStopIcon--stop';
      default:
        return 'unitArea__stateList__state__value__icon__runStopIcon--stop';
    }
  };

  /**
   * エラーコードに基づいてエラー出力とアイコンを表示するコンポーネント。
   *
   * @param error - 受信したエラーコード
   * @returns {JSX.Element} エラーコードに基づいた表示要素。
   */
  const ErrorDisplay = (error: string) => {
    if (error === '00') {
      return '―';
    }

    if (error === '100') {
      return `${trans('HCCVQDetail.item.value.error')}`;
    }

    if (error === '') {
      // 空文字は未課金状態なので非表示
      return <></>;
    }

    return (
      <Grid
        container
        columnSpacing={1}
        justifyContent="center"
        alignItems="center">
        <Grid item xs="auto">
          <img
            src={iconBlack.ERROR.src}
            alt={`${trans(
              `HCCVQDetail.item.image.alternativeText.${iconBlack.ERROR.alt}`
            )}`}
            className="unitArea__stateList__state__value__icon--error"
          />
        </Grid>
        <Grid item xs="auto">
          {`E${error}`}
        </Grid>
      </Grid>
    );
  };

  //空調機の種類が無い場合は表示しない
  if (unitType === '') {
    return <></>;
  }

  return (
    <Stack spacing={2}>
      <Box className="unitArea__title">
        <Box className="unitArea__title__name" title={unitName}>
          {`${trans('HCCVQDetail.title')} ${unitName}`}
        </Box>
      </Box>
      <Box className="unitArea__stateList">
        <Grid
          container
          alignItems="flex-end"
          justifyContent="center"
          rowSpacing={2}>
          {/** 運転/停止 */}
          <StateContents
            title={trans('HCCVQDetail.item.title.power')}
            value={
              runStop !== undefined ? (
                <Box className={runStopIconClassName(runStop)}>
                  <img
                    className="unitArea__stateList__state__value__icon__runStopIcon__smallImage"
                    src={runStopIconSource(runStop)}
                    title={`${trans(`HCCVQDetail.item.image.title.${runStop}`, {
                      defaultValue: initialUpper(runStop),
                    })}`}
                    alt={`${trans(
                      'HCCVQDetail.item.image.alternativeText.powerIcon'
                    )}`}
                  />
                </Box>
              ) : (
                <></>
              )
            }
            unitType={unitType}
          />
          {/** 蒸発温度 */}
          {unitType === 'hyozan' ? (
            <StateContents
              title={trans('HCCVQDetail.item.title.evaporatingTemp')}
              value={
                <>
                  {evaporating === ''
                    ? '--'
                    : invertComma(roundOff(evaporating, 1))}
                  <Box
                    component="span"
                    className="unitArea__stateList__state__value__unit--degreeC">
                    &nbsp;{trans('HCCVQDetail.item.unit.temperatureUnit')}
                  </Box>
                </>
              }
              unitType="hyozan"
            />
          ) : (
            <></>
          )}
          {/** コンプ高圧 */}
          <StateContents
            title={trans('HCCVQDetail.item.title.compressorHighPressure')}
            value={
              <>
                {highPressure === ''
                  ? '--'
                  : invertComma(roundOff(highPressure, 1))}
                <Box
                  component="span"
                  className="unitArea__stateList__state__value__unit--pascal">
                  &nbsp;
                  {pressureUnit === 'MPa'
                    ? trans('HCCVQDetail.item.unit.pressureUnit.mpa')
                    : trans('HCCVQDetail.item.unit.pressureUnit.bar')}
                </Box>
              </>
            }
            unitType={unitType}
          />
          {/** コンプ低圧 */}
          <StateContents
            title={trans('HCCVQDetail.item.title.compressorLowPressure')}
            value={
              <>
                {lowPressure === ''
                  ? '--'
                  : invertComma(roundOff(lowPressure, 1))}
                <Box
                  component="span"
                  className="unitArea__stateList__state__value__unit--pascal">
                  &nbsp;
                  {pressureUnit === 'MPa'
                    ? trans('HCCVQDetail.item.unit.pressureUnit.mpa')
                    : trans('HCCVQDetail.item.unit.pressureUnit.bar')}
                </Box>
              </>
            }
            unitType={unitType}
          />
          {/** 目標コンプ低圧 */}
          {unitType === 'hyozan' ? (
            <StateContents
              title={trans('HCCVQDetail.item.title.targetLowPressure')}
              value={
                <>
                  {targetLowPressure === ''
                    ? '--'
                    : invertComma(roundOff(targetLowPressure, 1))}
                  <Box
                    component="span"
                    className="unitArea__stateList__state__value__unit--pascal">
                    &nbsp;
                    {pressureUnit === 'MPa'
                      ? trans('HCCVQDetail.item.unit.pressureUnit.mpa')
                      : trans('HCCVQDetail.item.unit.pressureUnit.bar')}
                  </Box>
                </>
              }
              unitType="hyozan"
            />
          ) : (
            <></>
          )}
          {/** エラーコード*/}
          <StateContents
            title={trans('HCCVQDetail.item.title.error')}
            value={ErrorDisplay(error)}
            unitType={unitType}
          />
        </Grid>
      </Box>
    </Stack>
  );
};

/**
 * 状態値
 * @param props props
 * - title タイトル
 * - value 状態値
 * @returns JSX
 */
const StateContents = (props: {
  title: string;
  value: string | ReactElement;
  unitType: 'hyozan' | 'modbus';
}) => {
  const xs = props.unitType === 'hyozan' ? 4 : 3;

  return (
    <Grid item xs={xs} lg={2} className="unitArea__stateList__state">
      <Stack spacing={3}>
        <Box className="unitArea__stateList__state__title">{props.title}</Box>
        <Box className="unitArea__stateList__state__value">
          <Box>{props.value}</Box>
        </Box>
      </Stack>
    </Grid>
  );
};

export default HCCVQDetail;
